import React, { useState } from 'react';
import { formatNumber } from '../utils/pricingUtils';
import './CurrentPlanSummary.css';
import ConfirmationModal from '../../../components/common/ConfirmationModal';

/**
 * Displays a summary of the user's current plan with usage statistics
 */
const CurrentPlanSummary = ({
  currentPlan = 'Free',
  isCurrentActive = false,
  currentAgents = 1,
  currentQueries = 10,
  currentDocuments = 5,
  agentLimit = 1,
  queryLimit = 10,
  documentLimit = 5,
  isLoading = false,
  onCancelSubscription = null,
  subscriptionId = null,
  isCancelling = false
}) => {
  // State for confirmation modal
  const [showCancelModal, setShowCancelModal] = useState(false);

  // Calculate usage percentages
  const agentPercentage = Math.min(100, Math.round((currentAgents / Math.max(1, agentLimit)) * 100));
  const queryPercentage = Math.min(100, Math.round((currentQueries / Math.max(1, queryLimit)) * 100));
  const documentPercentage = Math.min(100, Math.round((currentDocuments / Math.max(1, documentLimit)) * 100));

  // Determine status color based on percentage
  const getStatusColor = (percentage) => {
    if (percentage >= 90) return 'critical';
    if (percentage >= 75) return 'warning';
    return 'normal';
  };

  // Handle cancel button click
  const handleCancelClick = () => {
    setShowCancelModal(true);
  };

  // Handle confirmation of cancellation
  const handleConfirmCancel = () => {
    if (onCancelSubscription) {
      onCancelSubscription();
    }
  };

  return (
    <div className="current-plan-summary">
      <h2>My Plan</h2>
      <div className="plan-summary-grid">
        <div className="plan-summary-item">
          <div className="summary-label">Current Plan</div>
          <div className="summary-value">{currentPlan}</div>
        </div>
        <div className="plan-summary-item">
          <div className="summary-label">Status</div>
          <div className="summary-value">{isCurrentActive ? 'Active' : 'Inactive'}</div>
        </div>

        <div className="plan-summary-item usage-item">
          <div className="summary-label">Agents</div>
          <div className="summary-value">
            <div className="usage-text">
              {currentAgents} / {agentLimit}
            </div>
            <div className="usage-bar-container">
              <div
                className={`usage-bar ${getStatusColor(agentPercentage)}`}
                style={{ width: `${agentPercentage}%` }}
              ></div>
            </div>
          </div>
        </div>

        <div className="plan-summary-item usage-item">
          <div className="summary-label">Queries</div>
          <div className="summary-value">
            <div className="usage-text">
              {formatNumber(currentQueries)} / {formatNumber(queryLimit)}
            </div>
            <div className="usage-bar-container">
              <div
                className={`usage-bar ${getStatusColor(queryPercentage)}`}
                style={{ width: `${queryPercentage}%` }}
              ></div>
            </div>
          </div>
        </div>

        <div className="plan-summary-item usage-item">
          <div className="summary-label">Documents</div>
          <div className="summary-value">
            <div className="usage-text">
              {formatNumber(currentDocuments)} / {formatNumber(documentLimit)}
            </div>
            <div className="usage-bar-container">
              <div
                className={`usage-bar ${getStatusColor(documentPercentage)}`}
                style={{ width: `${documentPercentage}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {isLoading && (
        <div className="usage-loading">Refreshing usage statistics...</div>
      )}

      {/* Show cancel button only for active paid plans */}
      {isCurrentActive && currentPlan !== 'Free' && subscriptionId && onCancelSubscription && (
        <div className="plan-actions">
          <button
            className="cancel-subscription-button"
            onClick={handleCancelClick}
            disabled={isCancelling}
          >
            {isCancelling ? 'Cancelling...' : 'Cancel Subscription'}
          </button>
        </div>
      )}

      {/* Confirmation Modal for cancellation */}
      <ConfirmationModal
        isOpen={showCancelModal}
        title="Cancel Subscription"
        message="Are you sure you want to cancel your subscription? You'll lose access to premium features at the end of your current billing period."
        confirmButtonText="Yes, Cancel Subscription"
        cancelButtonText="No, Keep Subscription"
        isLoading={isCancelling}
        onConfirm={handleConfirmCancel}
        onCancel={() => setShowCancelModal(false)}
      />
    </div>
  );
};

export default CurrentPlanSummary; 